import { useEffect, useRef, useState } from "react";
import type { Task, TaskExecution } from "./types";

const CASE_ORIGIN = "https://admin.skillbridge.co.jp";

/** 専用操作の種類ごとの Ops Admin の画面・記録後の知らせ・表示の文言。 */
export const EXECUTION_VIEWS: Record<TaskExecution["kind"], {
  path: string; message: string; idKey: string; title: string; statusNote: string; allow: string;
}> = {
  ca_phone: { path: "phone-tasks", message: "aile:ca-task-result", idKey: "taskId", title: "電話の発信・結果登録",
    statusNote: "電話対応の結果から反映されます。", allow: "microphone; autoplay" },
  ca_case: { path: "ca-cases", message: "aile:ca-case-result", idKey: "entryId", title: "応募案件の対応",
    statusNote: "応募案件の対応結果から反映されます。", allow: "microphone; autoplay" },
  x_reply: { path: "x-replies", message: "aile:sns-task-result", idKey: "id", title: "X 返信の送信・記録",
    statusNote: "X 返信の記録から反映されます。", allow: "clipboard-write" },
  video_upload: { path: "video-uploads", message: "aile:sns-task-result", idKey: "id", title: "動画の投稿・記録",
    statusNote: "動画投稿の記録から反映されます。", allow: "clipboard-write; autoplay" },
};

export function CaPhoneTaskPanel({ task, onRecorded }: {
  task: Task;
  onRecorded: (id: string) => Promise<void>;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const callback = useRef(onRecorded);
  callback.current = onRecorded;
  const [error, setError] = useState("");
  const execution = task.execution;
  const view = execution ? EXECUTION_VIEWS[execution.kind] : undefined;

  useEffect(() => {
    setError("");
    if (!view) return;
    let syncing = false;
    const receive = async (event: MessageEvent) => {
      if (event.origin !== CASE_ORIGIN || event.source !== frame.current?.contentWindow
        || event.data?.type !== view.message
        || (view.message === "aile:sns-task-result" && event.data.kind !== execution?.kind)
        || String(event.data[view.idKey]) !== execution?.source_id || syncing) return;
      syncing = true;
      try {
        await callback.current(task.id);
        setError("");
      } catch (e) {
        setError(e instanceof Error ? e.message : "対応状況を取得できませんでした。");
      } finally {
        syncing = false;
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [task.id, execution?.source_id, execution?.kind, view]);

  if (!execution || !view) return null;
  let url: URL;
  try { url = new URL(execution.url); } catch { return null; }
  if (url.origin !== CASE_ORIGIN || url.pathname !== `/${view.path}/${execution.source_id}/chat`) return null;

  return <section className="flex flex-col gap-2" aria-label={view.title}>
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold text-fg-primary">{view.title}</h3>
      <a href={url.href} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-700 underline">別画面で開く</a>
    </div>
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
    <iframe ref={frame} src={url.href} title={view.title}
      allow={view.allow} referrerPolicy="no-referrer"
      className="min-h-[70vh] w-full rounded-md border border-border-default bg-white" />
  </section>;
}
